import React, { useEffect, useState } from 'react';
import { Row, Col, Button, Alert, Typography, Space } from 'antd';
import { ReloadOutlined, ShareAltOutlined } from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import { Role } from '../constants/roles';
import { canApproveDashboardTile } from '../utils/approvalGates';
import { buildDashboardSections } from '../utils/dashboardLayout';
import { useProjectDashboard } from '../hooks/useProjectDashboard';
import { summariseRefreshResults } from '../utils/dashboardRefreshSummary';
import { getActiveImpersonation } from '../utils/impersonationFetch';
import DashboardTile from '../components/DashboardTile';
import ShareDashboardModal from '../components/ShareDashboardModal';
import MySharedDashboardLinks from '../components/MySharedDashboardLinks';
import DashboardBrandHeader from '../components/DashboardBrandHeader';
import PageShell from '../components/page/PageShell';
import PageState from '../components/page/PageState';
import DashboardTileApprovalModal from '../components/DashboardTileApprovalModal';

const { Title } = Typography;

// The API reports only fallbackUsed.logo (no logo stored, or its file could not be read), so the wording
// stays honest about both causes rather than claiming one.
function describeUnconfigured(parts) {
  return parts
    .map((p) => (p === 'logo' ? 'The company logo (missing or unreadable)' : `The company ${p} (not configured)`))
    .join('; ');
}

/**
 * Persona-adaptive project dashboard. Cites .claude/design/dashboard-redesign.md
 * ("Frontend additions": `pages/ProjectDashboard.jsx` — "Reads tile registry + snapshots").
 *
 * Reads `GET /api/v1/projects/:id/dashboard`, which the design doc states already returns only
 * the tiles the CALLING persona is allowed to see (server-side filtering — this component never
 * filters tiles client-side, per CLAUDE.md §6 "do not filter in the client").
 *
 * Two persona-gated affordances, UI-only mirrors of server-side ABAC (never the enforcement
 * boundary — CLAUDE.md §6):
 *   - "Refresh" — Lead Advisor / Admin only (dashboard-redesign.md "API surface": force refresh
 *     is "admin + LA").
 *   - "Generate shareable link" — Company SPOC only (dashboard-redesign.md §A.3: `company_spoc`
 *     is the only allowed creator role; `company_delegate` is explicitly denied server-side and
 *     never rendered here). Scoped to the SPOC's OWN project: `activeProject` is only ever a
 *     project this signed-in user is a member of (AppContext.selectProject's own membership
 *     check), so there is no separate "which project" picker needed here.
 *
 * Layout is persona-adaptive (utils/dashboardLayout.js): a Company SPOC leads with an engagement
 * headline, Lead Advisor / Advisor / Admin get operational sections. That is grouping and ordering
 * only; which tiles exist for a caller is decided by the API. A Company Delegate has no dashboard
 * (the API returns no tiles for that role), so the page says so instead of fetching and showing a
 * misleading "no tiles yet". The "My shared links" panel (list + revoke) is SPOC-only, or a Lead
 * Advisor acting as a SPOC through an active impersonation session.
 *
 * Branding (Commandment VIII): the read payload's `branding` is applied part by part; any part the
 * backend reports in `fallbackUsed` is left neutral and named in a "Company branding not configured"
 * notice shown to Lead Advisors and Advisors only.
 */
const ProjectDashboard = () => {
  const { user, activeProject } = useApp();
  const projectId = activeProject?.id;
  const { status, tiles, branding, refreshOutcomes, errorMessage, errorStatus, reload, refresh } = useProjectDashboard(projectId);
  const [shareOpen, setShareOpen] = useState(false);
  const [linksRefreshKey, setLinksRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [approvalTile, setApprovalTile] = useState(null);
  const [refreshNotice, setRefreshNotice] = useState(null); // { type, text } - dismissible, replaces the old 3s toast

  const isDelegate = user?.role === Role.COMPANY_DELEGATE;

  useEffect(() => {
    if (projectId && !isDelegate) reload();
    // NOTE: the exhaustive-deps hooks rule is not loaded by this build's ESLint config (see
    // .claude/lessons/eslint-plugin-react-hooks-breaks-cra-build.md), so a real suppression
    // comment for it would itself break the build — this plain-English note stands in for one
    // instead. Deliberately keyed on `projectId` only: `reload` is a new function reference each
    // render (it closes over `getApiToken`), and this effect should run once per project change,
    // not on every render.
  }, [projectId, isDelegate]);

  const isSpoc = user?.role === Role.COMPANY_SPOC;
  // Read on every render (never cached): an LA acting as a SPOC sees the SPOC's links panel.
  const impersonation = getActiveImpersonation();
  const actingAsSpoc =
    isSpoc ||
    (user?.role === Role.LEAD_ADVISOR &&
      String(impersonation?.effectiveRole || '').toLowerCase() === 'company_spoc' &&
      impersonation?.projectId === projectId);
  const isStaffReviewer = user?.role === Role.LEAD_ADVISOR || user?.role === Role.ADVISOR;
  const canRefresh = user?.role === Role.LEAD_ADVISOR || user?.role === Role.ADMIN;
  // Approving a tile is the Lead Advisor's act; platform_admin is refused 403 PLATFORM_ADMIN_APPROVAL_DENIED.
  const canApproveTiles = canApproveDashboardTile(user?.role);
  // Read on every render (never cached): the API refuses approval actions made through an
  // impersonation session, so the button is disabled up front with the reason.
  // Scoped to this project: an impersonation session on another project does not affect this one.
  const approvalDisabledReason = impersonation && impersonation.projectId === projectId
    ? 'Approving tiles is disabled while you are impersonating another user. End the impersonation to approve.'
    : null;

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshNotice(null);
    const result = await refresh();
    setRefreshing(false);
    if (result.inProgress) {
      setRefreshNotice({ type: 'info', text: 'A refresh is already in progress for this project - try again shortly.' });
    } else if (!result.ok) {
      setRefreshNotice({ type: 'error', text: result.errorMessage || 'Refresh failed.' });
    } else {
      const summary = summariseRefreshResults(result.results);
      setRefreshNotice({ type: summary.level, text: summary.text });
    }
  };

  if (!projectId) {
    return <PageState status="empty" message="Open a project to see its dashboard." />;
  }

  if (isDelegate) {
    return (
      <PageShell width="narrow" data-testid="dashboard-role-unavailable">
        <Alert
          type="info"
          showIcon
          message="The dashboard is not available for your role"
          description="Your tasks, training and meetings are available from the menu."
        />
      </PageShell>
    );
  }

  // Initial load only: a manual refresh re-reads silently (the hook keeps status 'ready').
  if (status === 'loading') {
    return <PageState status="loading" />;
  }

  if (status === 'error') {
    return (
      <PageShell width="narrow">
        <Alert type="warning" showIcon message={errorStatus === 403 ? "You do not have access to this dashboard" : errorStatus === 404 ? "Dashboard not found" : "The dashboard could not be loaded"} description={errorMessage} />
        <Button style={{ marginTop: 16 }} icon={<ReloadOutlined />} onClick={reload}>
          Retry
        </Button>
      </PageShell>
    );
  }

  const sections = buildDashboardSections(tiles, user?.role);

  return (
    <PageShell width="wide">
      <DashboardBrandHeader
        branding={branding}
        title="Project dashboard"
        subtitle={activeProject.companyName || activeProject.name}
        actions={(
          <Space>
            {canRefresh && (
              <Button icon={<ReloadOutlined />} loading={refreshing} onClick={handleRefresh}>
                Refresh
              </Button>
            )}
            {isSpoc && (
              <Button type="primary" icon={<ShareAltOutlined />} onClick={() => setShareOpen(true)}>
                Generate shareable link
              </Button>
            )}
          </Space>
        )}
      />

      {refreshNotice && (
        <Alert
          style={{ marginBottom: 16 }}
          type={refreshNotice.type}
          showIcon
          closable
          data-testid="refresh-summary"
          message={refreshNotice.text}
          onClose={() => setRefreshNotice(null)}
        />
      )}

      {isStaffReviewer && status !== 'error' && branding && branding.unconfigured.length > 0 && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          data-testid="branding-not-configured"
          message="Company branding not configured"
          description={`${describeUnconfigured(branding.unconfigured)} is not applied to this dashboard or its shared links. Set it up in Company settings.`}
        />
      )}

      {status === 'empty' && <PageState status="empty" message="No dashboard tiles yet for this project." />}

      {status === 'ready' &&
        sections.map((section) => (
          <div key={section.key} data-testid={`dashboard-section-${section.key}`} style={{ marginBottom: 24 }}>
            <Title level={4}>{section.title}</Title>
            <Row gutter={[16, 16]}>
              {section.tiles.map((tile) => (
                <Col key={tile.tileId} xs={24} sm={12} lg={8}>
                  <DashboardTile
                    tile={tile}
                    branding={branding}
                    refreshOutcome={refreshOutcomes[tile.tileId]}
                    onManageApproval={canRefresh ? setApprovalTile : undefined}
                    approvalDisabledReason={canRefresh ? approvalDisabledReason : undefined}
                  />
                </Col>
              ))}
            </Row>
          </div>
        ))}

      {canRefresh && approvalTile && (
        <DashboardTileApprovalModal
          open
          tile={approvalTile}
          projectId={projectId}
          canApprove={canApproveTiles}
          onClose={() => setApprovalTile(null)}
          onChanged={reload}
        />
      )}

      {isSpoc && (
        <ShareDashboardModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          projectId={projectId}
          tiles={tiles}
          onCreated={() => setLinksRefreshKey((k) => k + 1)}
        />
      )}

      {actingAsSpoc && <MySharedDashboardLinks projectId={projectId} tiles={tiles} refreshKey={linksRefreshKey} onCreateNew={isSpoc ? () => setShareOpen(true) : undefined} />}
    </PageShell>
  );
};

export default ProjectDashboard;
